/**
 * BerlinMap card — interactive map of Conny rent-reduction cases.
 *
 * Widget tag:
 *   <div data-conny-widget="berlin-map"
 *        data-city="berlin"
 *        data-zip="10439"            (optional, overrides storage)
 *        data-center-lat="52.52"     (optional)
 *        data-center-lng="13.405"    (optional)
 *        data-radius-km="5"          (optional, requires center-lat/lng)
 *        data-static-only="1"        (optional, skip API + use bundled data)
 *        data-search="off"           (optional, hide the in-map search bar)
 *        data-cluster="off"          (optional, raw dots instead of cluster bubbles)
 *        data-filter="won-with-savings" (optional, only buildings with cws>0)
 *        data-initial-center="52.53,13.41" (optional, opening view only — no filtering)
 *        data-initial-zoom="13"      (optional, opening view only — no filtering)
 *        data-color-mode="won"       (optional, all dots in the wins colour)
 *   ></div>
 *
 * Inputs in priority order (highest first):
 *   - data-attrs on the host div (always win — explicit author intent)
 *   - in-widget search bar (the user's manual focus pick)
 *   - sessionStorage city/zip — ONLY when `data-funnel-mode="1"` is set.
 *     Reading user-input storage outside funnel mode would make a public
 *     landing-page map jump around based on stale funnel answers from a
 *     previous tab, which is what we want to avoid.
 *   - hard-coded defaults
 *
 * Storage keys read:
 *   STORAGE_KEYS.city, STORAGE_KEYS.zip (only in funnel mode)
 *   STORAGE_KEYS.locale (always)
 *
 * Storage keys written: none — the map is read-only w.r.t. user state.
 *
 * Emits a `conny-map-stats` CustomEvent on the document whenever the
 * dataset is (re)loaded so the surrounding landing page can render the
 * same totals in its own tiles without duplicating the calculation.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { MapBounds } from "../../../shared/api";
import { fetchMapCases } from "../../../shared/cases-api";
import { CITY_CENTERS, type MapStatusFilter } from "../../../shared/cases-data";
import { useLocale, t } from "../../../shared/i18n";
import { STORAGE_KEYS, useStorage } from "../../../shared/storage";
import { AddressSearch, type PickedPlace } from "./AddressSearch";
import { MapCanvas } from "./MapCanvas";
import { MapLegend } from "./MapLegend";
import { MapStatsPanel } from "./MapStatsPanel";
import { type MapColorMode } from "./constants";
import { computeStats, pointsInScope, type MapScope } from "./stats";
import { geocodeAddress } from "./geocode";
import "./styles.css";

/** Radius (km) used to compute "near this address" stats after a search pick. */
const AREA_RADIUS_KM = 1;

export interface BerlinMapProps {
  /** Defaults to "berlin"; overridden by the host div's `data-city`. */
  city?: string;
  zip?: string;
  centerLat?: number;
  centerLng?: number;
  radiusKm?: number;
  /** Skip the API entirely and render the bundled static dataset. */
  staticOnly?: boolean;
  /**
   * Status filter applied to the dataset regardless of source (API or
   * bundled fallback) — see `MapStatusFilter`. The official replacement
   * for the landing page's window.fetch shim.
   */
  filter?: MapStatusFilter;
  /**
   * When true, read city/zip from sessionStorage (the Heyflow funnel
   * answers). When false (default), ignore storage entirely — the map is
   * driven only by data-attrs + the in-widget search bar. The Heyflow
   * snippet sets this to "1"; standalone landing pages leave it off.
   */
  funnelMode?: boolean;
  /**
   * Hide the in-map address search bar. Defaults to false in funnel mode
   * (user already typed their address) and true on standalone landing pages.
   * Override with data-search="on"/"off" on the host div.
   */
  showSearch?: boolean;
  /**
   * Cluster nearby dots into count bubbles at low zoom (default true) so
   * the city-wide view stays legible with the full ~10k-point dataset.
   * Disable with data-cluster="off" on the host div.
   */
  cluster?: boolean;
  /**
   * View-only initial framing: where the map *opens*, without dropping a
   * single point (unlike zip/center/radius, which scope the data). Lets an
   * embed start on a denser frame, e.g. the inner city.
   */
  initialCenter?: [number, number];
  initialZoom?: number;
  /**
   * Dot colouring — "status" (default) colours by open/closed balance;
   * "won" renders every dot in the wins blue for marketing embeds where
   * red would read as negative. Set via data-color-mode="won".
   */
  colorMode?: MapColorMode;
}

export function BerlinMapCard(props: BerlinMapProps = {}) {
  const locale = useLocale();
  // We always have to call the hook (rules of hooks), but we only consume
  // the value in funnel mode. In browse mode the storage value is ignored
  // so e.g. an old ZIP from a previous tab can't hijack a fresh page load.
  const [storedCity] = useStorage(STORAGE_KEYS.city);
  const [storedZip] = useStorage(STORAGE_KEYS.zip);
  const [storedStreet] = useStorage(STORAGE_KEYS.street);
  const [storedHouse] = useStorage(STORAGE_KEYS.house);

  const cityFromStorage = props.funnelMode ? storedCity : "";
  const zipFromStorage = props.funnelMode ? storedZip : "";

  const city = (props.city || cityFromStorage || "berlin").toLowerCase();
  const zip = props.zip || zipFromStorage || "";
  const cityMeta = CITY_CENTERS[city];

  // Funnel mode only: resolve the user's typed address (street/house/zip/city)
  // to a point so the result map opens centred on THEIR street, not the ZIP
  // centroid. We never stored coordinates, only the address text — so geocode
  // it once via Nominatim. Gated on a real street so a bare ZIP falls back to
  // the ZIP-centroid focus below.
  const addressText =
    props.funnelMode && storedStreet
      ? [
          [storedStreet, storedHouse].filter(Boolean).join(" "),
          [zip, city].filter(Boolean).join(" "),
        ]
          .filter(Boolean)
          .join(", ")
      : "";
  const geoQuery = useQuery({
    queryKey: ["geocode-address", addressText, locale],
    queryFn: () => geocodeAddress(addressText, locale),
    enabled: Boolean(addressText),
    staleTime: 30 * 60_000,
  });

  // Load the full city-level dataset once. We deliberately do NOT pass the
  // ZIP / center / radius as filters here — the widget always loads the
  // full city payload and filters on the client below. This makes funnel
  // mode behave exactly like browse mode the moment the user starts
  // interacting (pan/zoom/search), which was the whole "feels different"
  // bug.
  const query = useQuery({
    queryKey: [
      "map-cases",
      city,
      props.staticOnly ? "static" : "api",
      props.filter ?? "all",
    ],
    queryFn: () =>
      fetchMapCases({
        city,
        staticOnly: props.staticOnly,
        filter: props.filter,
      }),
    staleTime: 5 * 60_000,
  });

  const dataset = query.data?.dataset;
  const allPoints = dataset?.points ?? [];

  // ── Focus / scope state ───────────────────────────────────────────────
  //
  // The widget has three input "drivers" that can move the map:
  //   1. The in-widget search bar (`manualPick`) — a *jump-only* hint: the
  //      map flies to the picked place once, then the user roams free. A
  //      pick never filters the points (that used to make "all the other
  //      dots vanish") and never locks the view.
  //   2. A one-shot initial ZIP/centroid hint coming from data-attrs or,
  //      in funnel mode, sessionStorage. This used to be a permanent
  //      filter; that's the bug — once the user starts interacting they
  //      expect zoom/pan to take over (same as browse mode).
  //   3. Free pan/zoom — the bounds reported by Leaflet's `moveend`.
  //
  // We collapse (2) into "initial focus only": when the dataset loads we
  // compute a focus once, then mark the hint consumed. Any later pan/zoom
  // or search pick replaces the focus and the ZIP scope is dropped.
  //
  // `seq` makes every pick distinct: MapCanvas keys its fly-to effect on
  // the focus *values*, so without it re-picking the same address after
  // panning away wouldn't jump back.
  const [manualPick, setManualPick] = useState<
    (PickedPlace & { seq: number }) | null
  >(null);
  const pickSeq = useRef(0);
  const [focusConsumed, setFocusConsumed] = useState(false);

  // Current Leaflet viewport, kept in state so we can recompute stats /
  // visible points on every pan/zoom. Null until the map first mounts.
  const [viewBounds, setViewBounds] = useState<MapBounds | null>(null);

  // Initial focus on the user's geocoded address (street-level, zoom 16) —
  // computed once, then consumed on the first pan/zoom. This is what makes the
  // result map open on their street rather than a whole district. Higher
  // priority than the ZIP centroid below.
  const initialAddressFocus = useMemo<{
    lat: number;
    lng: number;
    zoom?: number;
  } | null>(() => {
    if (focusConsumed) return null;
    const g = geoQuery.data;
    return g ? { lat: g.lat, lng: g.lng, zoom: 16 } : null;
  }, [geoQuery.data, focusConsumed]);

  // Fallback initial focus: the ZIP centroid (computed once, then consumed).
  // Used when there's no geocoded street. After the user pans, zooms, or
  // searches we don't recompute this — they're driving.
  const initialZipFocus = useMemo<{
    lat: number;
    lng: number;
    zoom?: number;
  } | null>(() => {
    if (!dataset || !zip || focusConsumed) return null;
    const matches = dataset.points.filter((p) => p.zip_code === zip);
    if (!matches.length) return null;
    const lat = matches.reduce((s, p) => s + p.lat, 0) / matches.length;
    const lng = matches.reduce((s, p) => s + p.lng, 0) / matches.length;
    return { lat, lng, zoom: 14 };
  }, [dataset, zip, focusConsumed]);

  // The one-shot initial focus actually in effect (address wins over ZIP).
  const initialFocus = initialAddressFocus ?? initialZipFocus;

  // The focus we hand to MapCanvas. Order: a fresh search pick > the one-shot
  // address/ZIP hint > nothing (let the user roam free).
  const focus = manualPick
    ? {
        lat: manualPick.lat,
        lng: manualPick.lng,
        zoom: manualPick.kind === "city" ? (cityMeta?.zoom ?? 11) : 17,
        seq: manualPick.seq,
      }
    : initialFocus;

  // Active scope — single source of truth for stats title + visible points.
  // Search picks are deliberately ABSENT here: a pick only jumps the view
  // (see `focus` above); the stats then follow the viewport like any other
  // pan/zoom. Filtering on a pick is what made "all the other dots vanish".
  // Precedence (highest first):
  //   1. Geocoded user address (initial)     → "in this area" (small radius)
  //   2. One-shot ZIP focus not yet consumed → "in <zip>"
  //   3. Current viewport (if zoomed in vs. city bbox) → "in this view"
  //   4. City default                        → "in <city>"
  const scope: MapScope = useMemo(() => {
    if (initialAddressFocus) {
      return {
        kind: "area",
        lat: initialAddressFocus.lat,
        lng: initialAddressFocus.lng,
        radiusKm: AREA_RADIUS_KM,
      };
    }
    if (initialZipFocus && zip) return { kind: "zip", zip };
    if (viewBounds && cityMeta) {
      const citySpanLng = cityMeta.bbox[2] - cityMeta.bbox[0];
      const citySpanLat = cityMeta.bbox[3] - cityMeta.bbox[1];
      const viewSpanLng = viewBounds.east - viewBounds.west;
      const viewSpanLat = viewBounds.north - viewBounds.south;
      const zoomedIn =
        viewSpanLng < citySpanLng * 0.7 || viewSpanLat < citySpanLat * 0.7;
      if (zoomedIn) return { kind: "bounds", bounds: viewBounds };
    }
    return { kind: "city", city };
  }, [initialAddressFocus, initialZipFocus, zip, viewBounds, cityMeta, city]);

  // When the user pans/zooms, mark the initial hint consumed so it stops
  // dragging the map back to the address/ZIP centre on subsequent renders.
  function handleBoundsChange(b: MapBounds) {
    setViewBounds(b);
    if (!focusConsumed && initialFocus) {
      // Only consume once the user has actually moved away from the initial
      // centre — Leaflet's `moveend` fires after our programmatic setView too.
      const dLat = Math.abs((b.north + b.south) / 2 - initialFocus.lat);
      const dLng = Math.abs((b.east + b.west) / 2 - initialFocus.lng);
      if (dLat > 0.005 || dLng > 0.005) setFocusConsumed(true);
    }
  }

  // With jump-only picks there's no filter to clear, so Reset's job is to
  // fly back to the city overview. Modelled as a city-kind pick so it rides
  // the same focus path (and `seq` makes repeated resets fly every time).
  function resetToCity() {
    setManualPick(
      cityMeta
        ? {
            lat: cityMeta.center[0],
            lng: cityMeta.center[1],
            label: city,
            kind: "city",
            seq: ++pickSeq.current,
          }
        : null,
    );
    setFocusConsumed(true);
    setViewBounds(null);
  }

  const visiblePoints = useMemo(
    () => pointsInScope(allPoints, scope),
    [allPoints, scope],
  );
  const stats = useMemo(() => computeStats(visiblePoints), [visiblePoints]);

  // Emit the *visible* totals so the surrounding landing page tiles match
  // what's on the map right now (instead of always showing the full city).
  useEffect(() => {
    if (!dataset) return;
    document.dispatchEvent(
      new CustomEvent("conny-map-stats", {
        detail: { city, scope, stats, source: query.data?.source },
      }),
    );
  }, [dataset, city, scope, stats, query.data?.source]);

  // `initialCenter`/`initialZoom` only seed the map's opening view — they
  // are never re-applied and never filter the dataset (that's the whole
  // point: a denser opening frame with every point still on the map).
  const defaultCenter = props.initialCenter ??
    cityMeta?.center ?? [52.52, 13.405];
  const defaultZoom = props.initialZoom ?? cityMeta?.zoom ?? 11;
  const cityLabel = city.charAt(0).toUpperCase() + city.slice(1);
  // Show the reset pill only when there's somewhere to reset *from* —
  // i.e. the stats scope isn't already the whole city.
  const showReset = scope.kind !== "city";
  const showSearch = props.showSearch !== false;
  const showToolbar = showSearch;

  return (
    <div className="cn cn-map">
      {showToolbar && (
        <div className="cn-map-toolbar">
          {showSearch && (
            <AddressSearch
              city={city}
              bbox={dataset?.bbox ?? null}
              onPick={(p) => {
                setManualPick({ ...p, seq: ++pickSeq.current });
                setFocusConsumed(true);
              }}
            />
          )}
          {showReset && (
            <button
              type="button"
              className="cn-map-reset"
              onClick={resetToCity}
              aria-label={t("map.resetTo", { city: cityLabel }, locale)}
              title={t("map.resetTo", { city: cityLabel }, locale)}
            >
              <svg
                className="cn-map-reset__icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M3 12a9 9 0 1 0 3-6.7" />
                <path d="M3 4v5h5" />
              </svg>
              <span className="cn-map-reset__label">
                {t("map.resetTo", { city: cityLabel }, locale)}
              </span>
            </button>
          )}
        </div>
      )}
      <div className="cn-map-wrap">
        <MapCanvas
          points={visiblePoints}
          defaultCenter={defaultCenter}
          defaultZoom={defaultZoom}
          bbox={dataset?.bbox ?? null}
          focus={focus}
          onBoundsChange={handleBoundsChange}
          mode={props.cluster === false ? "dots" : "cluster"}
          colorMode={props.colorMode}
        />
        <MapStatsPanel stats={stats} scope={scope} />
        <MapLegend colorMode={props.colorMode} />
        {query.isLoading && (
          <div className="cn-map-loading">{t("map.loading", {}, locale)}</div>
        )}
        {query.isError && (
          <div className="cn-map-error">{t("map.loadError", {}, locale)}</div>
        )}
      </div>
    </div>
  );
}

/**
 * Adapter used by `mount.tsx` — reads widget options from the host
 * `<div data-conny-widget="berlin-map">`'s data-attributes so the same
 * component can be configured per-instance from plain HTML.
 */
export function BerlinMapMount({ host }: { host: HTMLElement }) {
  const num = (v: string | null): number | undefined => {
    if (v == null || v === "") return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  // In funnel mode the user already typed their address, so the search bar
  // is hidden by default. Standalone landing pages keep it visible.
  // Override either way with data-search="on"/"1" or data-search="off"/"0".
  const searchAttr = host.getAttribute("data-search");
  const funnelMode = host.getAttribute("data-funnel-mode") === "1";
  const filterAttr = host.getAttribute("data-filter");
  const filter: MapStatusFilter | undefined =
    filterAttr === "won-with-savings" ? filterAttr : undefined;
  // "lat,lng" pair for the view-only opening frame; ignored when malformed.
  const initialCenterAttr = host.getAttribute("data-initial-center");
  let initialCenter: [number, number] | undefined;
  if (initialCenterAttr) {
    const parts = initialCenterAttr.split(",").map((s) => Number(s.trim()));
    if (parts.length === 2 && parts.every(Number.isFinite)) {
      initialCenter = [parts[0]!, parts[1]!];
    }
  }
  const showSearch =
    searchAttr === null
      ? !funnelMode
      : searchAttr !== "off" && searchAttr !== "0";
  return (
    <BerlinMapCard
      city={host.getAttribute("data-city") || undefined}
      zip={host.getAttribute("data-zip") || undefined}
      centerLat={num(host.getAttribute("data-center-lat"))}
      centerLng={num(host.getAttribute("data-center-lng"))}
      radiusKm={num(host.getAttribute("data-radius-km"))}
      staticOnly={host.getAttribute("data-static-only") === "1"}
      filter={filter}
      initialCenter={initialCenter}
      initialZoom={num(host.getAttribute("data-initial-zoom"))}
      colorMode={
        host.getAttribute("data-color-mode") === "won" ? "won" : undefined
      }
      funnelMode={host.getAttribute("data-funnel-mode") === "1"}
      showSearch={showSearch}
      cluster={
        host.getAttribute("data-cluster") !== "off" &&
        host.getAttribute("data-cluster") !== "0"
      }
    />
  );
}
